import React, { useState } from 'react';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  VpnKey as KeyIcon,
  Build as ToolsIcon
} from '@mui/icons-material';
import AuthService, { User } from '../services/AuthService';

interface UserMenuProps {
  user: User;
  onLogout: () => void;
  onAdminPage: () => void;
  onJtlToolsPage?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout, onAdminPage, onJtlToolsPage }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const open = Boolean(anchorEl);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    await AuthService.logout();
    onLogout();
    handleClose();
  };

  const handleAdminPage = () => {
    onAdminPage();
    handleClose();
  };
  
  const handleJtlToolsPage = () => {
    if (onJtlToolsPage) {
      onJtlToolsPage();
      handleClose();
    }
  };

  const openChangePasswordDialog = () => {
    setPasswordDialogOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
    handleClose();
  };

  const handleChangePassword = async () => {
    // Reset states
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Bitte alle Felder ausfüllen');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Das neue Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    try {
      const response = await AuthService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        setSuccess('Passwort erfolgreich geändert');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Close dialog after a delay
        setTimeout(() => {
          setPasswordDialogOpen(false);
        }, 1500);
      } else {
        setError(response.error || 'Fehler beim Ändern des Passworts');
      }
    } catch (err) {
      setError('Fehler beim Ändern des Passworts');
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {getInitials(user.username)}
        </Avatar>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary={user.username} 
            secondary={user.role === 'admin' ? 'Administrator' : 'Benutzer'} 
          />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={openChangePasswordDialog}>
          <ListItemIcon>
            <KeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Passwort ändern" />
        </MenuItem>
        
        
        {user.role === 'admin' && (
          <MenuItem onClick={handleAdminPage}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Benutzerverwaltung" />
          </MenuItem>
        )}
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </MenuItem>
      </Menu>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Passwort ändern</DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}
          
          {success && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography color="success.main">{success}</Typography>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            id="current-password"
            label="Aktuelles Passwort"
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            id="new-password"
            label="Neues Passwort"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            id="confirm-password"
            label="Neues Passwort bestätigen"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleChangePassword} color="primary">
            Passwort ändern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserMenu;